import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Globe, Copy, Download, Maximize2, Check, AlertCircle, Rocket, Target, AlertTriangle, TrendingUp, Shield, ArrowUp, ArrowDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import { humanizeColumnName } from '../../lib/formatters';
import { DataQualityIndicator } from '../AnalysisEnhancements';
import { computeVarianceBands, roundToHundred, COLORS } from '../../lib/analysisUtils';
import { useReportingMode } from '../../context/ReportingModeContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ErrorBar, Cell } from 'recharts';
import ResultMethodNote from '../reporting/ResultMethodNote';
import ResultSummary from '../reporting/ResultSummary';
import InsightCallout from '../reporting/InsightCallout';

export function OverviewTab({
  aggregation, simulation, orderedResponseScale, scaleColors, scaleColorMap,
  pieData, webContextPack, allBreakdowns, population,
  copiedData, copyChartData, exportToCSV, setFullscreenChart,
}) {
  const { isMarketLab } = useReportingMode();
  const [showVarianceBands, setShowVarianceBands] = useState(false);

  // Primary Result rule: when probability-weighted (expected) data exists, it IS
  // the official result. Sampled is only available in Reliability tab.
  const hasExpected = !!aggregation?.expected_percentages && Object.keys(aggregation.expected_percentages).length > 0;
  const useExpected = hasExpected; // primary chart always uses expected when available

  const varianceBands = useMemo(
    () => computeVarianceBands(allBreakdowns, orderedResponseScale),
    [allBreakdowns, orderedResponseScale]
  );

  // Bar chart data for variance bands view
  const varianceBandData = useMemo(() => {
    if (!aggregation?.percentages || !varianceBands) return [];
    return orderedResponseScale.map(resp => ({
      name: resp,
      pct: aggregation.percentages[resp] || 0,
      min: varianceBands[resp]?.min || 0,
      max: varianceBands[resp]?.max || 0,
      std: varianceBands[resp]?.std || 0,
      range: [(aggregation.percentages[resp] || 0) - (varianceBands[resp]?.min || 0), (varianceBands[resp]?.max || 0) - (aggregation.percentages[resp] || 0)],
    }));
  }, [aggregation, varianceBands, orderedResponseScale]);

  // Rounded percentages that sum to exactly 100
  const activePercentages = useExpected ? aggregation?.expected_percentages : aggregation?.percentages;
  const activeDistribution = useExpected ? aggregation?.expected_distribution : aggregation?.distribution;
  const roundedPcts = useMemo(() => {
    if (!activePercentages) return {};
    return roundToHundred(activePercentages);
  }, [activePercentages]);

  // Full ordered data: includes ALL response options (even 0%) for legend alignment
  const allResponseData = useMemo(() => {
    if (!activeDistribution) return [];
    return orderedResponseScale.map(r => ({
      name: r,
      value: activeDistribution?.[r] || 0,
      rawPct: activePercentages?.[r] || 0,
      roundedPct: roundedPcts[r] || 0,
      color: scaleColorMap[r] || '#94A3B8',
    }));
  }, [activeDistribution, activePercentages, orderedResponseScale, roundedPcts, scaleColorMap]);

  const totalResponses = allResponseData.reduce((s, e) => s + e.value, 0);

  return (
    <div className="space-y-5">
      {/* Low Diversity Warning */}
      {(simulation.collapse_flag || aggregation?.collapse_flag) && (
        <div className="flex items-start gap-3 p-3 bg-warning-soft border border-warning-border rounded-lg">
          <AlertCircle className="w-4 h-4 text-warning-soft-foreground mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-warning-soft-foreground">Low Diversity Detected</p>
            <p className="text-xs text-warning-soft-foreground mt-0.5">Response distribution shows lower entropy than expected. Consider adding context documents or adjusting temperature settings.</p>
          </div>
        </div>
      )}

      <DataQualityIndicator aggregation={aggregation} population={population} />

      {/* Plain-English Result Summary — answers "what happened?" before charts */}
      <ResultSummary aggregation={aggregation} simulation={simulation} />

      {/* Context + coverage are now shown in <ReportHeader/> at the top of the Analysis page,
          so the duplicate metadata strip here is intentionally removed. */}

      {/* ─── Executive Snapshot (MarketLab) ─────────────────────────────── */}
      {isMarketLab && aggregation?.percentages && allBreakdowns && (() => {
        const pcts = aggregation.percentages || {};
        const scale = orderedResponseScale.length > 0 ? orderedResponseScale : Object.keys(pcts);
        if (scale.length < 3) return null;

        const midIdx = Math.floor(scale.length / 2);
        const positive = scale.slice(midIdx + 1).reduce((s, k) => s + (pcts[k] || 0), 0);
        const negative = scale.slice(0, midIdx).reduce((s, k) => s + (pcts[k] || 0), 0);

        // Adoption potential
        const adoptionPotential = positive > 50 ? 'High' : positive > 30 ? 'Medium' : 'Limited';
        const adoptionColor = positive > 50 ? 'text-positive-soft-foreground' : positive > 30 ? 'text-warning-soft-foreground' : 'text-negative-soft-foreground';
        const adoptionBg = positive > 50 ? 'bg-positive-soft' : positive > 30 ? 'bg-warning-soft' : 'bg-negative-soft';

        // Launch recommendation
        let launchRec, launchColor, launchBg;
        if (positive > 50 && negative < 20) { launchRec = 'Go'; launchColor = 'text-positive-soft-foreground'; launchBg = 'bg-positive-soft border-positive-border'; }
        else if (positive > 35) { launchRec = 'Conditional Go'; launchColor = 'text-warning-soft-foreground'; launchBg = 'bg-warning-soft border-warning-border'; }
        else { launchRec = 'Refine Messaging'; launchColor = 'text-negative-soft-foreground'; launchBg = 'bg-negative-soft border-negative-border'; }

        // Confidence from reliability
        const confScore = simulation?.confidence_score || (simulation?.pre_mode_used ? 60 : null);
        let confLabel = 'Moderate', confColor = 'text-warning-soft-foreground';
        if (confScore >= 75) { confLabel = 'High'; confColor = 'text-positive-soft-foreground'; }
        else if (confScore != null && confScore < 45) { confLabel = 'Low'; confColor = 'text-negative-soft-foreground'; }

        // Best / worst segments
        let bestSeg = null, worstSeg = null, bestDelta = -Infinity, worstDelta = Infinity;
        for (const [dn, dd] of Object.entries(allBreakdowns)) {
          if (!dd?.breakdown) continue;
          for (const [g, d] of Object.entries(dd.breakdown)) {
            if (!d?.percentages) continue;
            const segPos = scale.slice(midIdx + 1).reduce((s, k) => s + (d.percentages[k] || 0), 0);
            const delta = segPos - positive;
            if (delta > bestDelta) { bestDelta = delta; bestSeg = { name: g, attr: dn, positive: segPos }; }
            if (delta < worstDelta) { worstDelta = delta; worstSeg = { name: g, attr: dn, positive: segPos }; }
          }
        }

        return (
          <Card className={cn("border-2", launchBg)} data-testid="executive-snapshot-card">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-2 mb-3">
                <Rocket className="w-4 h-4 text-indigo-600" />
                <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">Executive Snapshot</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="rounded-lg border p-2.5" data-testid="exec-launch-rec">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">Launch Recommendation</p>
                  <p className={cn("text-sm font-bold", launchColor)}>{launchRec}</p>
                </div>
                <div className="rounded-lg border p-2.5" data-testid="exec-primary-target">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">Primary Target</p>
                  <p className="text-sm font-bold text-slate-800 dark:text-slate-100">{bestSeg?.name || '—'}</p>
                  {bestSeg && <p className="text-[10px] text-muted-foreground">{humanizeColumnName(bestSeg.attr)} (+{bestDelta.toFixed(0)}pp)</p>}
                </div>
                <div className="rounded-lg border p-2.5" data-testid="exec-risk-segment">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">Main Risk Segment</p>
                  <p className="text-sm font-bold text-slate-800 dark:text-slate-100">{worstSeg?.name || '—'}</p>
                  {worstSeg && <p className="text-[10px] text-negative-soft-foreground">{humanizeColumnName(worstSeg.attr)} ({worstDelta.toFixed(0)}pp)</p>}
                </div>
                <div className="rounded-lg border p-2.5" data-testid="exec-adoption-potential">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">Adoption Potential</p>
                  <p className={cn("text-sm font-bold", adoptionColor)}>{adoptionPotential}</p>
                  <p className="text-[10px] text-muted-foreground">{positive.toFixed(0)}% favorable</p>
                </div>
                <div className="rounded-lg border p-2.5" data-testid="exec-confidence">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-0.5">Confidence</p>
                  <p className={cn("text-sm font-bold", confColor)}>{confLabel}</p>
                  {confScore != null && <p className="text-[10px] text-muted-foreground">Score: {confScore}</p>}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* ─── Top Drivers of Support & Resistance ─────────────────────────── */}
      {isMarketLab && aggregation?.percentages && allBreakdowns && Object.keys(allBreakdowns).length > 0 && (() => {
        const pcts = aggregation.percentages || {};
        const scale = orderedResponseScale.length > 0 ? orderedResponseScale : Object.keys(pcts);
        if (scale.length < 3) return null;
        const midIdx = Math.floor(scale.length / 2);
        const overallPos = scale.slice(midIdx + 1).reduce((s, k) => s + (pcts[k] || 0), 0);

        const segments = [];
        for (const [dn, dd] of Object.entries(allBreakdowns)) {
          if (!dd?.breakdown) continue;
          for (const [g, d] of Object.entries(dd.breakdown)) {
            if (!d?.percentages || (d.count || 0) < 5) continue;
            const segPos = scale.slice(midIdx + 1).reduce((s, k) => s + (d.percentages[k] || 0), 0);
            segments.push({ name: g, attr: dn, delta: segPos - overallPos, positive: segPos });
          }
        }
        segments.sort((a, b) => b.delta - a.delta);

        const topSupport = segments.filter(s => s.delta > 0).slice(0, 3);
        const topResist = segments.filter(s => s.delta < 0).sort((a, b) => a.delta - b.delta).slice(0, 3);

        if (topSupport.length === 0 && topResist.length === 0) return null;

        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="support-resistance-drivers">
            {/* Support drivers */}
            <Card className="border border-positive-border/60">
              <CardContent className="pt-3 pb-3">
                <div className="flex items-center gap-2 mb-2.5">
                  <ArrowUp className="w-3.5 h-3.5 text-positive-soft-foreground" />
                  <span className="text-xs font-semibold text-positive-soft-foreground">Top Drivers of Support</span>
                </div>
                <div className="space-y-2">
                  {topSupport.length > 0 ? topSupport.map((s, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`support-driver-${i}`}>
                      <span className="text-[10px] font-bold text-positive-soft-foreground w-4">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-slate-800 dark:text-slate-200 truncate">{s.name}</p>
                        <p className="text-[10px] text-muted-foreground">{humanizeColumnName(s.attr)}</p>
                      </div>
                      <Badge variant="outline" className="text-[10px] border-positive-border text-positive-soft-foreground shrink-0">+{s.delta.toFixed(0)}pp</Badge>
                    </div>
                  )) : <p className="text-[10px] text-muted-foreground">No strong support drivers detected</p>}
                </div>
              </CardContent>
            </Card>

            {/* Resistance drivers */}
            <Card className="border border-negative-border/60">
              <CardContent className="pt-3 pb-3">
                <div className="flex items-center gap-2 mb-2.5">
                  <ArrowDown className="w-3.5 h-3.5 text-negative-soft-foreground" />
                  <span className="text-xs font-semibold text-negative-soft-foreground">Top Drivers of Resistance</span>
                </div>
                <div className="space-y-2">
                  {topResist.length > 0 ? topResist.map((s, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`resistance-driver-${i}`}>
                      <span className="text-[10px] font-bold text-negative-soft-foreground w-4">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-slate-800 dark:text-slate-200 truncate">{s.name}</p>
                        <p className="text-[10px] text-muted-foreground">{humanizeColumnName(s.attr)}</p>
                      </div>
                      <Badge variant="outline" className="text-[10px] border-negative-border text-negative-soft-foreground shrink-0">{s.delta.toFixed(0)}pp</Badge>
                    </div>
                  )) : <p className="text-[10px] text-muted-foreground">No strong resistance drivers detected</p>}
                </div>
              </CardContent>
            </Card>
          </div>
        );
      })()}

      {/* Web Context Panel */}
      {webContextPack && (
        <Card className="border border-blue-200 bg-blue-50/30" data-testid="web-context-used-panel">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 mb-2">
              <Globe className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">External Context (Public Sources)</span>
              <Badge variant="outline" className="text-[10px] border-blue-200 text-blue-700">{webContextPack.mode === 'snapshot' ? 'Snapshot' : 'Auto-refresh'}</Badge>
            </div>
            <div className="grid grid-cols-4 gap-3 text-xs">
              <div><p className="text-blue-600">Recency</p><p className="font-medium text-blue-900">Last {webContextPack.recency_window_days} days</p></div>
              <div><p className="text-blue-600">Sources</p><p className="font-medium text-blue-900">{webContextPack.sources?.length || 0}</p></div>
              <div><p className="text-blue-600">Claims</p><p className="font-medium text-blue-900">{webContextPack.claims?.length || 0}</p></div>
              <div><p className="text-blue-600">Retrieved</p><p className="font-medium text-blue-900">{webContextPack.retrieved_at ? new Date(webContextPack.retrieved_at).toLocaleDateString() : '—'}</p></div>
            </div>
            {webContextPack.sources?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {webContextPack.sources.slice(0, 6).map((src, i) => (
                  <span key={i} className="text-[10px] px-1.5 py-0.5 bg-white rounded border border-blue-200 text-blue-600">{src.publisher}</span>
                ))}
                {webContextPack.sources.length > 6 && <span className="text-[10px] text-blue-500">+{webContextPack.sources.length - 6} more</span>}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Distribution Chart — most visually prominent element */}
        <Card className="border border-border chart-animate lg:col-span-2 bg-gradient-to-br from-white via-white to-slate-50/80 dark:from-slate-900 dark:to-slate-800/50" data-testid="response-distribution-card">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <div className="flex flex-col gap-0.5 min-w-0">
              <CardTitle className="text-lg font-bold">Primary Result</CardTitle>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">
                {hasExpected
                  ? 'Aggregated across all simulated individuals, accounting for response uncertainty.'
                  : 'Aggregated across all simulated individuals.'}
              </p>
            </div>
            <div className="flex items-center gap-1">
              {/* Variance Bands Toggle */}
              {allBreakdowns && Object.keys(allBreakdowns).length > 0 && (
                <div className="flex items-center gap-1.5 mr-2 border-r border-slate-200 pr-2">
                  <span className="text-[10px] text-slate-500">Variance</span>
                  <Switch
                    checked={showVarianceBands}
                    onCheckedChange={setShowVarianceBands}
                    className="data-[state=checked]:bg-primary h-4 w-7"
                    data-testid="variance-bands-toggle"
                  />
                </div>
              )}
              <Button variant="ghost" size="sm" onClick={copyChartData} className="h-8 w-8 p-0" title="Copy data">
                {copiedData ? <Check className="w-4 h-4 text-positive-soft-foreground" /> : <Copy className="w-4 h-4" />}
              </Button>
              <Button variant="ghost" size="sm" onClick={exportToCSV} className="h-8 w-8 p-0" title="Export CSV">
                <Download className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setFullscreenChart('distribution')} className="h-8 w-8 p-0" title="Fullscreen">
                <Maximize2 className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {allResponseData.some(d => d.value > 0) ? (
              <div>
                {showVarianceBands && varianceBandData.length > 0 ? (
                  /* Variance Bands Bar Chart */
                  <div className="space-y-3">
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={varianceBandData} barCategoryGap="20%">
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis dataKey="name" tick={{ fontSize: 11, fontWeight: 500 }} />
                        <YAxis unit="%" tick={{ fontSize: 11 }} />
                        <Tooltip
                          formatter={(value, name, props) => {
                            const d = props.payload;
                            return [`${Number(value).toFixed(1)}% (range: ${d.min.toFixed(1)}–${d.max.toFixed(1)}%)`, 'Overall'];
                          }}
                          contentStyle={{ backgroundColor: '#0F172A', border: 'none', borderRadius: '6px' }}
                          labelStyle={{ color: '#fff' }}
                          itemStyle={{ color: '#fff' }}
                        />
                        <Bar dataKey="pct" radius={[4, 4, 0, 0]}>
                          {varianceBandData.map((entry) => (
                            <Cell key={`cell-${entry.name}`} fill={scaleColorMap[entry.name] || '#94A3B8'} />
                          ))}
                          <ErrorBar dataKey="range" width={8} strokeWidth={2} stroke="#475569" />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    <p className="text-[10px] text-slate-400 text-center">
                      Error bars show the min–max range across all demographic subgroups. Wider bars = more disagreement between groups.
                    </p>
                  </div>
                ) : (
                  /* Standard Stacked Bar — clean, color-only with rich hover */
                  <div className="space-y-3">
                    <div className="flex h-11 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-600" data-testid="response-bar">
                      {allResponseData.map((entry) => {
                        const pct = totalResponses > 0 ? (entry.value / totalResponses * 100) : 0;
                        if (pct <= 0) return null;
                        return (
                          <div
                            key={entry.name}
                            className="flex items-center justify-center transition-all duration-500 relative group"
                            style={{ width: `${pct}%`, backgroundColor: entry.color, minWidth: pct > 2 ? '24px' : '4px' }}
                            data-testid={`bar-segment-${entry.name}`}
                          >
                            {/* Hover tooltip — primary info source */}
                            <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-[11px] px-2.5 py-1.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10 shadow-lg">
                              <span className="font-semibold">{entry.name}</span> · {entry.roundedPct}% ({entry.value.toLocaleString()})
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {/* Legend — flex-wrap, no clipping */}
                    <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs" data-testid="primary-result-legend">
                      {allResponseData.map((entry) => (
                        <div key={entry.name} className="flex items-center gap-1.5 min-w-0" data-testid={`legend-item-${entry.name}`}>
                          <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: entry.color }} />
                          <span className="text-slate-600 dark:text-slate-300 font-medium">{entry.name}</span>
                          <span className="text-slate-900 dark:text-slate-100 font-semibold tabular-nums">{entry.roundedPct}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-400">No data available</div>
            )}
            <ResultMethodNote
              mode={hasExpected ? 'uncertainty_aware' : 'sampled'}
              className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-700"
            />
            {(() => {
              const sorted = [...allResponseData].filter(d => d.value > 0).sort((a, b) => b.rawPct - a.rawPct);
              if (sorted.length < 2) return null;
              const top = sorted[0];
              const second = sorted[1];
              const lead = top.rawPct - second.rawPct;
              let tone = 'info';
              let text;
              if (lead < 5) {
                tone = 'warning';
                text = `Top responses are close: "${top.name}" leads "${second.name}" by only ${lead.toFixed(1)}pp — interpret with caution.`;
              } else if (top.rawPct >= 50) {
                tone = 'positive';
                text = `"${top.name}" dominates with ${top.rawPct.toFixed(0)}% — clear majority view.`;
              } else {
                tone = 'info';
                text = `"${top.name}" leads with ${top.rawPct.toFixed(0)}%, ahead of "${second.name}" by ${lead.toFixed(1)}pp.`;
              }
              return (
                <InsightCallout
                  label="What to notice"
                  text={text}
                  tone={tone}
                  className="mt-3"
                  testId="primary-result-callout"
                />
              );
            })()}
          </CardContent>
        </Card>

        {/* Response Summary */}
        <Card className="border border-border chart-animate" style={{ animationDelay: '0.2s' }}>
          <CardHeader className="pb-2"><CardTitle className="text-lg">Response Summary</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {allResponseData.length > 0
                ? allResponseData.map((entry) => (
                    <div key={entry.name} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-slate-700 dark:text-slate-200">{entry.name}</span>
                        <span className="text-slate-500 dark:text-slate-400">{entry.value} ({entry.rawPct?.toFixed(1)}%)</span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${entry.rawPct}%`, backgroundColor: entry.color }} />
                      </div>
                    </div>
                  ))
                : aggregation?.percentages && aggregation?.distribution && Object.entries(aggregation.percentages).sort((a, b) => b[1] - a[1]).map(([response, pct], idx) => (
                    <div key={response} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-slate-700 dark:text-slate-200">{response}</span>
                        <span className="text-slate-500 dark:text-slate-400">{aggregation.distribution[response]} ({pct.toFixed(1)}%)</span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: COLORS[idx % COLORS.length] }} />
                      </div>
                    </div>
                  ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Key Insights Panel */}
      {aggregation && (
        <Card className="border border-border" data-testid="key-insights-panel">
          <CardContent className="pt-4 pb-3">
            <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3">Key Insights</p>
            <div className="space-y-2">
              {(() => {
                const ins = [];
                const pcts = aggregation.percentages || {};
                const dist = aggregation.distribution || {};
                const scale = orderedResponseScale.length > 0 ? orderedResponseScale : Object.keys(pcts);
                if (scale.length >= 3) {
                  const midIdx = Math.floor(scale.length / 2);
                  const positive = scale.slice(midIdx + 1).reduce((s, k) => s + (pcts[k] || 0), 0);
                  const negative = scale.slice(0, midIdx).reduce((s, k) => s + (pcts[k] || 0), 0);
                  const neutral = pcts[scale[midIdx]] || 0;
                  if (positive > negative + 15) ins.push({ icon: '↑', color: 'text-positive-soft-foreground', bg: 'bg-positive-soft', text: `Overall sentiment is positive (${positive.toFixed(0)}% favorable vs ${negative.toFixed(0)}% unfavorable).` });
                  else if (negative > positive + 15) ins.push({ icon: '↓', color: 'text-negative-soft-foreground', bg: 'bg-negative-soft', text: `Overall sentiment is negative (${negative.toFixed(0)}% unfavorable vs ${positive.toFixed(0)}% favorable).` });
                  else if (neutral > 40) ins.push({ icon: '—', color: 'text-warning-soft-foreground', bg: 'bg-warning-soft', text: `Opinion is undecided — ${neutral.toFixed(0)}% responded neutral, with slim margins between positive (${positive.toFixed(0)}%) and negative (${negative.toFixed(0)}%).` });
                  else ins.push({ icon: '⟷', color: 'text-blue-700', bg: 'bg-blue-50', text: `Opinions are fragmented — ${positive.toFixed(0)}% positive, ${negative.toFixed(0)}% negative, ${neutral.toFixed(0)}% neutral.` });
                }
                const sorted = Object.entries(pcts).sort((a, b) => b[1] - a[1]);
                if (sorted.length >= 2) ins.push({ icon: '#', color: 'text-primary', bg: 'bg-blue-50', text: `"${sorted[0][0]}" is the dominant response at ${sorted[0][1].toFixed(0)}%, followed by "${sorted[1][0]}" at ${sorted[1][1].toFixed(0)}%.` });
                if (allBreakdowns && Object.keys(allBreakdowns).length > 0) {
                  let maxDiv = 0, label = '';
                  for (const [dn, dd] of Object.entries(allBreakdowns)) {
                    if (!dd?.breakdown) continue;
                    for (const [g, d] of Object.entries(dd.breakdown)) {
                      if (!d?.percentages) continue;
                      const gs = Object.entries(d.percentages).sort((a, b) => b[1] - a[1]);
                      if (gs.length > 0) { const diff = Math.abs(gs[0][1] - (pcts[gs[0][0]] || 0)); if (diff > maxDiv) { maxDiv = diff; label = `Strongest divergence: "${g}" (${humanizeColumnName(dn)}) over-indexes on "${gs[0][0]}" by ${diff.toFixed(0)}pp vs overall.`; } }
                    }
                  }
                  if (label && maxDiv > 5) ins.push({ icon: '△', color: 'text-orange-700', bg: 'bg-orange-50', text: label });
                }
                if (scale.length >= 4) { const eN = pcts[scale[0]] || 0; const eP = pcts[scale[scale.length - 1]] || 0; if (eN > 15 && eP > 15) ins.push({ icon: '!', color: 'text-negative-soft-foreground', bg: 'bg-negative-soft', text: `Polarization detected — ${eN.toFixed(0)}% at "${scale[0]}" and ${eP.toFixed(0)}% at "${scale[scale.length - 1]}".` }); }
                return ins.length > 0 ? ins.map((i, idx) => (
                  <div key={idx} className={cn("flex items-start gap-2.5 text-sm rounded-md px-3 py-2", i.bg)}>
                    <span className={cn("font-bold text-xs mt-0.5 w-4 text-center shrink-0", i.color)}>{i.icon}</span>
                    <span className={cn("leading-snug", i.color)}>{i.text}</span>
                  </div>
                )) : <p className="text-sm text-muted-foreground">Insufficient data for insight generation.</p>;
              })()}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Variability + Structural Drivers */}
      {aggregation && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Variability Snapshot — Enhanced with consensus metrics */}
          <Card className="border border-border" data-testid="variability-snapshot">
            <CardContent className="pt-4 pb-3">
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-3">Variability Snapshot</p>
              {(() => {
                const pcts = aggregation.percentages || {};
                const scale = orderedResponseScale.length > 0 ? orderedResponseScale : Object.keys(pcts);
                const n = scale.length;
                if (n < 2) return <p className="text-xs text-muted-foreground">Not enough data</p>;
                let mean = 0, variance = 0;
                for (let i = 0; i < n; i++) { mean += ((pcts[scale[i]] || 0) / 100) * (i / (n - 1)); }
                for (let i = 0; i < n; i++) { const p = (pcts[scale[i]] || 0) / 100; variance += p * (i / (n - 1) - mean) ** 2; }
                const dispersion = (Math.sqrt(variance) * 100).toFixed(1);
                const maxEnt = Math.log2(n);
                let entropy = 0;
                for (const k of scale) { const p = (pcts[k] || 0) / 100; if (p > 0) entropy -= p * Math.log2(p); }
                const normEnt = maxEnt > 0 ? (entropy / maxEnt * 100) : 0;
                const normEntStr = normEnt.toFixed(0);

                // Consensus level derived from diversity index
                const consensusScore = 100 - normEnt;
                let consensusLabel, consensusColor, consensusBg;
                if (consensusScore >= 60) { consensusLabel = 'High'; consensusColor = 'text-positive-soft-foreground'; consensusBg = 'bg-positive-soft'; }
                else if (consensusScore >= 35) { consensusLabel = 'Moderate'; consensusColor = 'text-warning-soft-foreground'; consensusBg = 'bg-warning-soft'; }
                else { consensusLabel = 'Low'; consensusColor = 'text-negative-soft-foreground'; consensusBg = 'bg-negative-soft'; }

                // Confidence score from dominant response
                const sorted = Object.entries(pcts).sort((a, b) => b[1] - a[1]);
                const dominantPct = sorted[0]?.[1] || 0;
                const confidenceScore = Math.min(100, Math.round(dominantPct * 1.5));
                let confLabel, confColor;
                if (confidenceScore >= 65) { confLabel = 'High'; confColor = 'text-positive-soft-foreground'; }
                else if (confidenceScore >= 40) { confLabel = 'Moderate'; confColor = 'text-warning-soft-foreground'; }
                else { confLabel = 'Low'; confColor = 'text-negative-soft-foreground'; }

                // Interpretation text
                let interpretation;
                if (consensusScore >= 60) {
                  interpretation = `Strong consensus detected. Responses cluster around "${sorted[0]?.[0] || ''}" with ${dominantPct.toFixed(0)}% concentration.`;
                } else if (consensusScore >= 35) {
                  interpretation = `Moderate consensus. Responses show medium dispersion across groups, with some clustering around "${sorted[0]?.[0] || ''}".`;
                } else {
                  interpretation = `Low consensus. Responses are dispersed across options with no clear majority view.`;
                }

                return (
                  <div className="space-y-4">
                    {/* Top metrics row */}
                    <div className="grid grid-cols-3 gap-2.5">
                      <div className="text-center p-2.5 rounded-lg border border-slate-100 dark:border-slate-700" data-testid="variability-consensus">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Consensus</p>
                        <p className={cn("text-xl font-bold", consensusColor)}>{consensusLabel}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{consensusScore.toFixed(0)}%</p>
                      </div>
                      <div className="text-center p-2.5 rounded-lg border border-slate-100 dark:border-slate-700" data-testid="variability-dispersion">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Dispersion</p>
                        <p className="text-xl font-bold text-slate-800 dark:text-slate-100">{dispersion}%</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">spread</p>
                      </div>
                      <div className="text-center p-2.5 rounded-lg border border-slate-100 dark:border-slate-700" data-testid="variability-confidence">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">Confidence</p>
                        <p className={cn("text-xl font-bold", confColor)}>{confLabel}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{confidenceScore}%</p>
                      </div>
                    </div>

                    {/* Consensus gauge */}
                    <div className="space-y-1.5" data-testid="variability-gauge">
                      <div className="flex justify-between text-[10px] text-muted-foreground font-medium">
                        <span>Consensus</span>
                        <span>Fragmented</span>
                      </div>
                      <div className="h-3 rounded-full bg-gradient-to-r from-emerald-200 via-amber-200 to-red-200 relative overflow-hidden">
                        {/* Fill indicator */}
                        <div
                          className="absolute top-0 left-0 h-full rounded-full opacity-30 transition-all duration-700"
                          style={{
                            width: `${normEnt}%`,
                            background: consensusScore >= 60 ? '#10b981' : consensusScore >= 35 ? '#f59e0b' : '#ef4444',
                          }}
                        />
                        <div
                          className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white border-2 shadow-md transition-all duration-700"
                          style={{
                            left: `calc(${Math.min(95, Math.max(5, normEnt))}% - 8px)`,
                            borderColor: consensusScore >= 60 ? '#10b981' : consensusScore >= 35 ? '#f59e0b' : '#ef4444',
                          }}
                        />
                      </div>
                      <p className="text-[10px] text-center text-muted-foreground">
                        Diversity Index: {normEntStr}%
                      </p>
                    </div>

                    {/* Interpretation */}
                    <div className={cn("rounded-lg px-3 py-2.5 text-xs leading-relaxed", consensusBg)} data-testid="variability-interpretation">
                      <span className={cn("font-semibold", consensusColor)}>Consensus Level: {consensusLabel}.</span>{' '}
                      <span className={consensusColor}>{interpretation}</span>
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>

          {/* Structural Drivers */}
          <Card className="border border-border" data-testid="structural-drivers-preview">
            <CardContent className="pt-4 pb-3">
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1">Key Demographic Drivers</p>
              <p className="text-[11px] text-muted-foreground mb-3">Which attributes most influence how people respond</p>
              {(() => {
                const pcts = aggregation.percentages || {};
                const scale = orderedResponseScale.length > 0 ? orderedResponseScale : Object.keys(pcts);
                const src = allBreakdowns || {};
                if (Object.keys(src).length === 0) return <p className="text-xs text-muted-foreground">Loading demographic signals...</p>;
                const drivers = [];
                for (const [dn, dd] of Object.entries(src)) {
                  if (!dd?.breakdown) continue;
                  let mxD = 0, tg = '';
                  for (const [g, d] of Object.entries(dd.breakdown)) {
                    if (!d?.percentages) continue;
                    let div = 0;
                    for (const k of scale) div += Math.abs((d.percentages[k] || 0) - (pcts[k] || 0));
                    div /= 2;
                    if (div > mxD) { mxD = div; tg = g; }
                  }
                  drivers.push({ name: dn, divergence: mxD, topGroup: tg });
                }
                drivers.sort((a, b) => b.divergence - a.divergence);
                const top5 = drivers.slice(0, 5);
                const maxDiv = top5[0]?.divergence || 1;
                return top5.length > 0 ? (
                  <div className="space-y-3">
                    {top5.map(d => (
                      <div key={d.name} className="space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-medium text-slate-700 dark:text-slate-200">{humanizeColumnName(d.name)}</span>
                          <span className="text-muted-foreground">
                            <span className="font-semibold text-slate-600 dark:text-slate-300">{d.divergence.toFixed(1)}%</span>
                            <span className="text-slate-400 ml-1">gap — driven by <span className="font-medium text-slate-500 dark:text-slate-300">{d.topGroup}</span></span>
                          </span>
                        </div>
                        <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(d.divergence / maxDiv) * 100}%`, backgroundColor: d.divergence > 12 ? '#ef4444' : d.divergence > 7 ? '#f59e0b' : '#3b82f6' }} />
                        </div>
                      </div>
                    ))}
                    <p className="text-[10px] text-slate-400 pt-1 border-t border-slate-100 dark:border-slate-700">Higher gap = this attribute strongly shapes opinion.</p>
                  </div>
                ) : <p className="text-xs text-muted-foreground">No demographic signals detected.</p>;
              })()}
            </CardContent>
          </Card>
        </div>
      )}

    </div>
  );
}

function DistributionTable({ label, percentages, ordered }) {
  if (!percentages) return null;
  return (
    <div className="rounded border border-slate-100 dark:border-slate-700">
      <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
        <p className="text-[11px] font-semibold text-slate-700 dark:text-slate-200">{label}</p>
      </div>
      <div className="p-2 space-y-1">
        {ordered.map((opt) => (
          <div key={opt} className="flex items-center justify-between text-xs">
            <span className="text-slate-600 dark:text-slate-300 truncate">{opt}</span>
            <span className="text-slate-700 dark:text-slate-100 font-mono tabular-nums">
              {(percentages[opt] || 0).toFixed(1)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
